import { FlatTreeNode, TreeNode } from '@treetop/shared/model';

export const flattenTree = <T extends TreeNode<unknown>>(
  nodes: T[],
  level = 0,
  parentId: string | null = null,
  expandedNodes: T[] = [],
  selectedNodes: T[] = [],
  disabledNodes: T[] = [],
): FlatTreeNode<T>[] => {
  nodes = nodes.map((node) => ({ ...node, children: node.children ? [...node.children] : [] }));

  return nodes
    .sort((a, b) => {
      if (a.order && b.order) {
        return a.order - b.order;
      }

      return 0;
    })
    .flatMap((node, index) => [
      {
        node: { ...node, parentId, children: node.children, order: index },
        level,
        expandable: !!node.children?.length,
        isExpanded: expandedNodes.some((n) => n.id === node.id),
        selected: selectedNodes.some((n) => n.id === node.id),
        disabled: disabledNodes.some((n) => n.id === node.id),
      },
      ...(node.children ? flattenTree(node.children as T[], level + 1, node.id, expandedNodes, selectedNodes, disabledNodes) : []),
    ]);
};

export const findNodeById = <T extends TreeNode<unknown>>(id: string, nodes: T[]): T | undefined => {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }

    if (node.children) {
      const foundNode = findNodeById(id, node.children as T[]);

      if (foundNode) {
        return foundNode;
      }
    }
  }

  return undefined;
};

export const findNodesByIds = <T extends TreeNode<unknown>>(ids: string[], nodes: T[]): T[] => {
  const idSet = new Set(ids);
  const foundNodes: T[] = [];

  const traverse = (node: T) => {
    if (node.id && idSet.has(node.id)) {
      foundNodes.push(node);
      idSet.delete(node.id);
    }
    if (node.children && idSet.size > 0) {
      for (const child of node.children as T[]) {
        traverse(child);
        if (idSet.size === 0) break;
      }
    }
  };

  for (const node of nodes) {
    traverse(node);
    if (idSet.size === 0) break;
  }

  return foundNodes;
};

export const shouldRender = <T extends TreeNode<unknown>>(node: FlatTreeNode<T>, nodes: FlatTreeNode<T>[]): boolean => {
  const getParentNode = (node: TreeNode<unknown>) => nodes.find((n) => n.node.id === node.parentId);

  let parent = getParentNode(node.node);
  while (parent) {
    if (!parent.isExpanded) {
      return false;
    }
    parent = getParentNode(parent.node);
  }
  return true;
};

export const flattenTreeToMap = <T extends { uniqueId?: string | null; children?: T[] }>(nodes: T[]): Map<string, T> => {
  const map = new Map<string, T>();

  function recurse(items: T[]) {
    for (const item of items) {
      if (item.uniqueId) {
        map.set(item.uniqueId, item);
      }

      if (item.children && item.children.length > 0) {
        recurse(item.children);
      }
    }
  }

  recurse(nodes.filter((node) => !!node));

  return map;
};

export const collectAllChildren = (children: TreeNode<unknown>[], disabledIds: Set<string>) => {
  children.forEach((child) => {
    disabledIds.add(child.id);
    if (child.children && child.children.length > 0) {
      collectAllChildren(child.children, disabledIds);
    }
  });
};
